"use client";

import { Loader2, ShieldCheck } from "lucide-react";
import type {
  FAKE_CITATION_CHECKER_STRINGS,
  VerifyVariant,
} from "@/components/tools/fake-citation-checker";
import type {
  BibliographyReferenceResult,
  BibliographyReport,
  ReferenceVerdict,
} from "@/lib/bibliography-report";
import type { ReferenceExtraction } from "@/lib/document-extract";
import { t } from "@/lib/i18n/interpolate";
import type { MetadataFinding } from "@/lib/metadata-conflicts";
import type { ReferenceIdentifier } from "@/lib/reference-identifiers";
import type {
  TitleLookupResult,
  TitleMatch,
} from "@/lib/reference-title-lookup";

/**
 * The presentational half of the reference checker shared by `/verify-bibliography`
 * and the Fake Citation Checker tool. Every word comes from `strings`, so a translated
 * tool page renders it in the page language.
 */
export type ReferenceCheckStrings = {
  -readonly [K in keyof typeof FAKE_CITATION_CHECKER_STRINGS]: string;
};
type Format = (value: number) => string;

const VERDICT_CLASSES: Record<ReferenceVerdict, string> = {
  verified: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  mismatch: "bg-amber-500/15 text-amber-800 dark:text-amber-300",
  "not-found": "bg-destructive/10 text-destructive",
  unchecked: "bg-muted text-muted-foreground",
};

export function verdictLabel(
  strings: ReferenceCheckStrings,
  verdict: ReferenceVerdict
): string {
  const labels: Record<ReferenceVerdict, string> = {
    verified: strings.verdictVerified,
    mismatch: strings.verdictMismatch,
    "not-found": strings.verdictNotFound,
    unchecked: strings.verdictUnchecked,
  };
  return labels[verdict];
}

function provenance(
  strings: ReferenceCheckStrings,
  identifier: ReferenceIdentifier
): string {
  const labels: Record<ReferenceIdentifier["kind"], string> = {
    doi: strings.provenanceDoi,
    pmid: strings.provenancePmid,
    isbn: strings.provenanceIsbn,
    arxiv: strings.provenanceArxiv,
  };
  return labels[identifier.kind];
}

function findingText(
  strings: ReferenceCheckStrings,
  finding: MetadataFinding
): string {
  const values = {
    written: finding.written ?? "",
    authoritative: finding.authoritative ?? "",
  };
  if (finding.rule === "year-conflict") {
    return t(strings.findingYear, values);
  }
  if (finding.rule === "title-conflict") {
    return strings.findingTitle;
  }
  if (finding.rule === "metadata-incomplete") {
    return strings.findingIncomplete;
  }
  return finding.message;
}

function providerRecordUrl(identifier: ReferenceIdentifier): string {
  if (identifier.kind === "doi") {
    return `https://doi.org/${identifier.value}`;
  }
  if (identifier.kind === "pmid") {
    return `https://pubmed.ncbi.nlm.nih.gov/${identifier.value}/`;
  }
  if (identifier.kind === "isbn") {
    return `https://openlibrary.org/isbn/${identifier.value}`;
  }
  return `https://arxiv.org/abs/${identifier.value}`;
}

export function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-xs">{label}</p>
    </div>
  );
}

export function ManuscriptFlags({
  extraction,
  strings,
}: {
  extraction: ReferenceExtraction;
  strings: ReferenceCheckStrings;
}) {
  const hasFlags =
    extraction.missingFromList.length > 0 || extraction.neverCited.length > 0;
  return (
    <section className="space-y-3">
      <h3 className="font-semibold text-foreground">
        {strings.crossCheckHeading}
      </h3>
      {hasFlags ? null : (
        <p className="rounded-lg border border-border bg-card p-4 text-muted-foreground text-sm">
          {strings.crossCheckClean}
        </p>
      )}
      {extraction.missingFromList.length > 0 ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
          <p className="font-medium text-sm">{strings.missingFromList}</p>
          <ul className="mt-2 list-disc space-y-1 ps-5 text-muted-foreground text-sm">
            {extraction.missingFromList.map((citation) => (
              <li key={`${citation.surname}-${citation.year}`}>
                {citation.raw}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {extraction.neverCited.length > 0 ? (
        <div className="rounded-lg border border-sky-500/40 bg-sky-500/5 p-4">
          <p className="font-medium text-sm">{strings.neverCited}</p>
          <ul className="mt-2 list-disc space-y-1 ps-5 text-muted-foreground text-sm">
            {extraction.neverCited.map((reference) => (
              <li key={reference}>{reference}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function lookupHeading(
  strings: ReferenceCheckStrings,
  lookup: Extract<TitleLookupResult, { ok: true }>,
  status: BibliographyReferenceResult["status"],
  source: string
): string {
  const sources =
    lookup.searched.length > 1 ? strings.bothIndexes : lookup.searched[0];
  const headings: Record<TitleMatch["verdict"], string> = {
    found:
      status === "not-checked"
        ? t(strings.headingFound, { source })
        : t(strings.headingFoundElsewhere, { source }),
    close: t(strings.headingClose, { source }),
    "not-found": t(strings.headingNearest, { sources }),
  };
  return headings[lookup.verdict];
}

function TitleLookupDetails({
  lookup,
  status,
  strings,
}: {
  lookup: TitleLookupResult;
  status: BibliographyReferenceResult["status"];
  strings: ReferenceCheckStrings;
}) {
  if (!lookup.ok) {
    const failures: Record<typeof lookup.reason, string> = {
      "rate-limited": strings.lookupRateLimited,
      unavailable: strings.lookupUnavailable,
      network: strings.lookupNetwork,
    };
    return (
      <p className="mt-2 text-muted-foreground text-sm">
        {failures[lookup.reason]}
      </p>
    );
  }
  const { candidate } = lookup;
  if (!candidate) {
    const sources =
      lookup.searched.length > 1 ? strings.bothIndexes : lookup.searched[0];
    return (
      <p className="mt-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
        {t(strings.noMatch, { sources })}
      </p>
    );
  }
  const differenceLabels: Record<TitleMatch["differences"][number], string> = {
    title: strings.differenceTitle,
    year: strings.differenceYear,
    author: strings.differenceAuthor,
  };
  const differences = lookup.differences.map((key) => differenceLabels[key]);
  const showContainer =
    candidate.container && candidate.container !== candidate.title;
  return (
    <div className="mt-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
      <p className="font-medium">
        {lookupHeading(strings, lookup, status, candidate.source)}
      </p>
      <p className="mt-1">
        {candidate.title}
        {showContainer ? ` — ${candidate.container}` : ""}
        {candidate.year ? `, ${candidate.year}` : ""}
      </p>
      {candidate.authors.length > 0 ? (
        <p className="mt-1 text-muted-foreground text-xs">
          {candidate.authors.slice(0, 4).join(", ")}
          {candidate.authors.length > 4 ? ` ${strings.etAl}` : ""}
        </p>
      ) : null}
      {differences.length > 0 && lookup.verdict !== "not-found" ? (
        <p className="mt-1 text-muted-foreground text-xs">
          {t(strings.differences, { list: differences.join("; ") })}
        </p>
      ) : null}
      {candidate.url ? (
        <a
          className="mt-1 inline-block text-primary text-xs underline-offset-4 hover:underline"
          href={candidate.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          {candidate.doi ? `doi:${candidate.doi}` : strings.openRecord} ↗
        </a>
      ) : null}
    </div>
  );
}

function RetrievedMetadata({
  metadata,
  strings,
}: {
  metadata: BibliographyReferenceResult["metadata"];
  strings: ReferenceCheckStrings;
}) {
  const rows = [
    [strings.retrievedTitle, metadata?.title],
    [strings.retrievedAuthor, metadata?.author],
    [strings.retrievedDate, metadata?.issued],
  ].filter((row): row is [string, string] => Boolean(row[1]));
  return rows.map(([label, value]) => (
    <p className="mt-1 text-sm" key={label}>
      <strong>{label}</strong> {value}
    </p>
  ));
}

function ReferenceResult({
  fmt,
  result,
  strings,
}: {
  fmt: Format;
  result: BibliographyReferenceResult;
  strings: ReferenceCheckStrings;
}) {
  const statusError =
    result.status === "not-checked"
      ? strings.errorNoIdentifier
      : strings.errorUnresolved;
  return (
    <li className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-sm">
          {t(strings.referenceNumber, { n: fmt(result.position) })}
        </p>
        <span
          className={`rounded-full px-2 py-1 font-medium text-xs ${VERDICT_CLASSES[result.verdict]}`}
        >
          {verdictLabel(strings, result.verdict)}
        </span>
      </div>
      <p className="mt-2 text-muted-foreground text-sm">{result.reference}</p>
      {result.identifier ? (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span>
            <strong>{strings.sourceLabel}</strong>{" "}
            {provenance(strings, result.identifier)} ·{" "}
            {result.identifier.kind.toUpperCase()} {result.identifier.value}
          </span>
          <a
            className="text-primary underline-offset-4 hover:underline"
            href={providerRecordUrl(result.identifier)}
            rel="noopener noreferrer"
            target="_blank"
          >
            {strings.openProviderRecord} ↗
          </a>
        </div>
      ) : null}
      <RetrievedMetadata metadata={result.metadata} strings={strings} />
      {result.findings.map((finding) => (
        <p
          className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm"
          key={finding.rule}
        >
          {findingText(strings, finding)}
        </p>
      ))}
      {result.error && !result.titleLookup ? (
        <p className="mt-2 text-muted-foreground text-sm">{statusError}</p>
      ) : null}
      {result.titleLookup ? (
        <TitleLookupDetails
          lookup={result.titleLookup}
          status={result.status}
          strings={strings}
        />
      ) : null}
    </li>
  );
}

function privacyNote(
  strings: ReferenceCheckStrings,
  report: BibliographyReport
): string {
  if (report.results.some((result) => result.titleLookup)) {
    return strings.privacyTitles;
  }
  if (report.summary.identifiersFound > 0) {
    return strings.privacyIdentifiers;
  }
  return strings.privacyNone;
}

export function MetadataResults({
  fmt,
  report,
  skippedNote,
  strings,
}: {
  fmt: Format;
  report: BibliographyReport;
  skippedNote: string | null;
  strings: ReferenceCheckStrings;
}) {
  return (
    <section aria-live="polite" className="space-y-4">
      <div>
        <h3 className="font-semibold text-foreground">
          {strings.resultsHeading}
        </h3>
        <p className="mt-1 text-muted-foreground text-sm">
          {privacyNote(strings, report)}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={strings.verdictVerified}
          value={fmt(report.summary.verified)}
        />
        <Stat
          label={strings.verdictMismatch}
          value={fmt(report.summary.mismatch)}
        />
        <Stat
          label={strings.verdictNotFound}
          value={fmt(report.summary.notFound)}
        />
        <Stat
          label={strings.verdictUnchecked}
          value={fmt(report.summary.unchecked)}
        />
      </div>
      <ol className="space-y-3">
        {report.results.map((result) => (
          <ReferenceResult
            fmt={fmt}
            key={`${result.position}-${result.reference}`}
            result={result}
            strings={strings}
          />
        ))}
      </ol>
      {skippedNote ? (
        <p className="text-muted-foreground text-sm">{skippedNote}</p>
      ) : null}
      <p className="text-muted-foreground text-xs leading-relaxed">
        {strings.caveat}
      </p>
    </section>
  );
}

export function ActionRow({
  busy,
  limitNote,
  onSample,
  onVerify,
  progress,
  referenceCount,
  strings,
  variant,
}: {
  busy: boolean;
  limitNote: string | null;
  onSample: () => void;
  onVerify: () => void;
  progress: string | null;
  referenceCount: number;
  strings: ReferenceCheckStrings;
  variant: VerifyVariant;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        className="rounded-lg border border-border bg-card px-4 py-2 font-medium text-sm"
        onClick={onSample}
        type="button"
      >
        {strings.trySample}
      </button>
      <button
        className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm disabled:opacity-50"
        disabled={busy || referenceCount === 0}
        onClick={onVerify}
        type="button"
      >
        {busy ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <ShieldCheck className="size-4" />
        )}
        {variant === "ai" ? strings.checkReferences : strings.verifyReferences}
      </button>
      {progress ? (
        <p aria-live="polite" className="text-muted-foreground text-sm">
          {progress}
        </p>
      ) : null}
      {limitNote && !progress ? (
        <p className="text-muted-foreground text-xs">{limitNote}</p>
      ) : null}
    </div>
  );
}
